import React, { useState } from "react"
import "./ContactPage.css"

const EMPTY_FORM = {
  subject: "",
  name: "",
  email: "",
  phone: "",
  expertise: "",
  message: "",
}

const ContactPage = () => {
  const [form, setForm] = useState(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState(null) // { type: 'success' | 'error', message }
  const [preview, setPreview] = useState(null)

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!e.target.reportValidity()) return

    const data = {
      subject: form.subject.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      expertise: form.expertise.trim(),
      message: form.message.trim(),
    }

    setPreview(data)
    setStatus(null)
    setSubmitting(true)

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      })

      let result = {}
      try {
        result = await response.json()
      } catch (_) {
        // no JSON body
      }

      if (!response.ok || result.success !== true) {
        throw new Error(result.message || "Submission failed")
      }

      setStatus({
        type: "success",
        message: "Thank you. Your inquiry has been submitted successfully.",
      })
      setForm(EMPTY_FORM)
    } catch (error) {
      setStatus({
        type: "error",
        message:
          "We could not submit your inquiry. Please try again or contact us directly at info@energypulseworld.com.",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="epw-contact-page">

      <main>
        <header className="epw-hero">
          <div className="epw-wrap">
            <p className="epw-eyebrow">Contact Us</p>
            <h1>How can we help?</h1>
            <p>
              Send us your question about EnergyPulseWorld topics, geographic coverage,
              subscriptions or support. Our team will review your inquiry and respond accordingly.
            </p>
          </div>
        </header>

        <section className="epw-main">
          <div className="epw-wrap epw-layout">

            <aside className="epw-side">
              <div className="epw-side-card">
                <p className="epw-eyebrow" style={{ color: "#ffb2bf" }}>EnergyPulseWorld</p>
                <h2>Get in touch</h2>
                <p>Use this form for questions concerning:</p>
                <ul>
                  <li>Research topics and data</li>
                  <li>Regional and country coverage</li>
                  <li>Pro and Enterprise subscriptions</li>
                  <li>Technical or account support</li>
                </ul>
                <p className="epw-email">info@energypulseworld.com</p>
              </div>
            </aside>

            <section className="epw-form-card">
              <h2>Send an inquiry</h2>
              <p>Fields marked with an asterisk are required.</p>

              <form onSubmit={handleSubmit} noValidate={false}>
                <div className="epw-form-grid">

                  <div className="epw-field epw-field-full">
                    <label htmlFor="subject">
                      Subject <span className="epw-required">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={form.subject}
                      onChange={handleChange("subject")}
                    >
                      <option value="">Select a subject</option>
                      <option value="Topics">Topics</option>
                      <option value="Coverage">Coverage</option>
                      <option value="Subscription">Subscription</option>
                      <option value="Support">Support</option>
                    </select>
                  </div>

                  <div className="epw-field">
                    <label htmlFor="name">
                      Name <span className="epw-required">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      required
                      value={form.name}
                      onChange={handleChange("name")}
                    />
                  </div>

                  <div className="epw-field">
                    <label htmlFor="email">
                      Email <span className="epw-required">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={form.email}
                      onChange={handleChange("email")}
                    />
                  </div>

                  <div className="epw-field">
                    <label htmlFor="phone">
                      Phone <span className="epw-required">*</span>
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      required
                      value={form.phone}
                      onChange={handleChange("phone")}
                    />
                  </div>

                  <div className="epw-field">
                    <label htmlFor="expertise">
                      Area of expertise <span className="epw-optional">(optional)</span>
                    </label>
                    <input
                      id="expertise"
                      name="expertise"
                      type="text"
                      placeholder="e.g. Energy, Trade, Finance, Manufacturing"
                      value={form.expertise}
                      onChange={handleChange("expertise")}
                    />
                  </div>

                  <div className="epw-field epw-field-full">
                    <label htmlFor="message">
                      Inquiry <span className="epw-required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      placeholder="Please describe your question or request..."
                      value={form.message}
                      onChange={handleChange("message")}
                    />
                    <span className="epw-help">
                      Please avoid including confidential, payment or password information.
                    </span>
                  </div>

                </div>

                <div className="epw-actions">
                  <p className="epw-privacy">
                    By submitting this form, you consent to EnergyPulseWorld using the
                    information provided to respond to your inquiry.
                  </p>

                  <button className="epw-submit" type="submit" disabled={submitting}>
                    {submitting ? "Submitting..." : "Submit inquiry"}
                  </button>
                </div>

                {status && (
                  <div className={`epw-status ${status.type}`} role="status" aria-live="polite">
                    {status.message}
                  </div>
                )}

                {preview && (
                  <div className="epw-preview" style={{ display: "block" }}>
                    <h3>Submission summary</h3>
                    <table aria-label="Inquiry summary">
                      <tbody>
                        <tr><th>Subject</th><td>{preview.subject}</td></tr>
                        <tr><th>Name</th><td>{preview.name}</td></tr>
                        <tr><th>Email</th><td>{preview.email}</td></tr>
                        <tr><th>Phone</th><td>{preview.phone}</td></tr>
                        <tr><th>Area of expertise</th><td>{preview.expertise || "\u2014"}</td></tr>
                        <tr><th>Inquiry</th><td style={{ whiteSpace: "pre-wrap" }}>{preview.message}</td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </form>
            </section>

          </div>
        </section>
      </main>
    </div>
  )
}

export default ContactPage
